"use client";

import Link from "next/link";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/utils/utils";

import type { VariantProps } from "class-variance-authority";
import type { ReactNode } from "react";

type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

type ErrorStateLinkAction = {
  href: string;
  label: string;
  variant?: ButtonVariant;
  type?: "link";
};

type ErrorStateButtonAction = {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
  type?: "button";
};

type ErrorStateAction = ErrorStateLinkAction | ErrorStateButtonAction;

interface ErrorStateProps {
  title: string;
  description: string;
  details?: ReactNode;
  actions?: ErrorStateAction[];
  className?: string;
}

export function ErrorState({ title, description, details, actions, className }: ErrorStateProps) {
  return (
    <div
      className={cn(
        "flex min-h-[60vh] w-full items-center justify-center bg-background px-4 py-16",
        className
      )}
    >
      <Card className="w-full max-w-xl text-center">
        <CardHeader className="gap-4">
          <CardTitle className="text-3xl font-semibold text-foreground">{title}</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            {description}
          </CardDescription>
        </CardHeader>
        {details ? (
          <CardContent className="space-y-4 text-left text-sm text-muted-foreground">
            {details}
          </CardContent>
        ) : null}
        {actions?.length ? (
          <CardFooter className="flex flex-wrap items-center justify-center gap-3">
            {actions.map((action, index) => {
              if ("href" in action && action.href) {
                return (
                  <Button
                    key={`${action.label}-${index}`}
                    asChild
                    variant={action.variant ?? "outline"}
                  >
                    <Link href={action.href}>{action.label}</Link>
                  </Button>
                );
              }

              return (
                <Button
                  key={`${action.label}-${index}`}
                  onClick={"onClick" in action ? action.onClick : undefined}
                  variant={action.variant ?? "default"}
                >
                  {action.label}
                </Button>
              );
            })}
          </CardFooter>
        ) : null}
      </Card>
    </div>
  );
}
